import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { motion, AnimatePresence } from 'framer-motion'
import HeaderLogo from './icons/header-logo'
import Container from '@comp/ui/container'
import Button from '@comp/ui/button'
import { useGlobalContext } from '../context/GlobalContext'

interface SiteHeaderProps {
  isMenuOpen: boolean
  toggleMenu: () => void
}

type Localized = { en: string; tr: string }

function useLang(): 'en' | 'tr' {
  const { locale } = useRouter()
  return locale === 'tr' ? 'tr' : 'en'
}

const navLabels = {
  products: { en: 'Products', tr: 'Ürünler' },
  solutions: { en: 'Solutions', tr: 'Çözümler' },
  integrations: { en: 'Integrations', tr: 'Entegrasyonlar' },
  pricing: { en: 'Pricing', tr: 'Fiyatlandırma' },
  resources: { en: 'Resources', tr: 'Kaynaklar' },
  company: { en: 'Company', tr: 'Şirket' },
  talkToSales: { en: 'Talk to sales', tr: 'Satışla görüşün' },
  becomePartner: { en: 'Become a Partner', tr: 'İş Ortağı Olun' },
  startNow: { en: 'Start Now', tr: 'Hemen Başlayın' },
  openMenu: { en: 'Open menu', tr: 'Menüyü aç' },
  closeMenu: { en: 'Close menu', tr: 'Menüyü kapat' },
}

export default function SiteHeader({ isMenuOpen, toggleMenu }: SiteHeaderProps) {
  const { globalData } = useGlobalContext()
  const features = globalData?.features ?? []
  const lang = useLang()

  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 10)
    handle()
    window.addEventListener('scroll', handle, { passive: true })
    return () => window.removeEventListener('scroll', handle)
  }, [])

  return (
    <header
      className={`sticky top-0 z-50 transition duration-300 ease-smooth ${
        scrolled
          ? 'bg-surface/90 backdrop-blur-md border-b border-line-subtle'
          : 'bg-surface border-b border-transparent'
      }`}
    >
      <Container size="xl">
        <div className="flex h-16 items-center justify-between gap-2 sm:gap-6 xl:h-20 xl:gap-10">
          <div className="flex items-center gap-10">
            <Link
              href="/"
              aria-label="Monofor home"
              className="text-brand-900 transition hover:text-brand-800"
            >
              <HeaderLogo />
            </Link>

            <nav className="hidden items-center gap-1 xl:flex">
              <NavDropdown label={navLabels.products[lang]} width="wide" align="left">
                <ProductsMenu />
              </NavDropdown>
              <NavDropdown label={navLabels.solutions[lang]} width="wide" align="left">
                <SolutionsMenu />
              </NavDropdown>
              <NavLink href="/integrations">{navLabels.integrations[lang]}</NavLink>
              <NavLink href="/pricing">{navLabels.pricing[lang]}</NavLink>
              <NavDropdown label={navLabels.resources[lang]} width="wide-md" align="left">
                <ResourcesMenu features={features} />
              </NavDropdown>
              <NavLink href="/company">{navLabels.company[lang]}</NavLink>
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <Link
              href="/contact"
              className="hidden whitespace-nowrap text-body-sm text-ink-secondary hover:text-ink-primary xl:inline-flex xl:px-3 xl:py-2"
            >
              {navLabels.talkToSales[lang]}
            </Link>
            <div className="hidden xl:inline-flex">
              <Button href="/become-partner" variant="outline" size="md" className="whitespace-nowrap">
                {navLabels.becomePartner[lang]}
              </Button>
            </div>
            <Button href="/try/now" variant="primary" size="md" className="hidden whitespace-nowrap min-[360px]:inline-flex">
              {navLabels.startNow[lang]}
            </Button>
            <button
              onClick={toggleMenu}
              aria-label={isMenuOpen ? navLabels.closeMenu[lang] : navLabels.openMenu[lang]}
              aria-expanded={isMenuOpen}
              className="-mr-2 inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-primary transition hover:bg-surface-muted xl:hidden"
            >
              {isMenuOpen ? <CloseGlyph /> : <MenuGlyph />}
            </button>
          </div>
        </div>
      </Container>
    </header>
  )
}

// ---------------------------------------------------------------------------
// Desktop nav primitives
// ---------------------------------------------------------------------------

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="px-3 py-2 text-body-sm text-ink-secondary transition hover:text-ink-primary"
    >
      {children}
    </Link>
  )
}

function NavDropdown({
  label,
  width = 'narrow',
  align = 'left',
  children,
}: {
  label: string
  width?: 'narrow' | 'wide-md' | 'wide'
  align?: 'left' | 'center' | 'right'
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false)
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 px-3 py-2 text-body-sm text-ink-secondary transition hover:text-ink-primary"
      >
        {label}
        <ChevronGlyph
          className={`h-3.5 w-3.5 transition duration-200 ease-smooth ${
            open ? 'rotate-180 text-ink-primary' : ''
          }`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute top-full pt-3 ${
              align === 'center'
                ? 'left-1/2 -translate-x-1/2'
                : align === 'right'
                ? 'right-0'
                : 'left-0'
            }`}
          >
            <div
              className={`overflow-hidden rounded-2xl border border-line-subtle bg-surface shadow-elevated ${
                width === 'wide'
                  ? 'w-[720px]'
                  : width === 'wide-md'
                  ? 'w-[560px]'
                  : 'min-w-[300px]'
              }`}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Products mega-menu: three products with sub-capabilities
// ---------------------------------------------------------------------------

interface ProductGroup {
  name: string
  tagline: Localized
  href: string
  items: { label: Localized; href: string }[]
}

const productGroups: ProductGroup[] = [
  {
    name: 'Monosign',
    tagline: { en: 'IAM + IGA', tr: 'IAM + IGA' },
    href: '/sign',
    items: [
      { label: { en: 'Single Sign-On', tr: 'Tek Oturum Açma' }, href: '/sign/sso' },
      { label: { en: 'Multi-Factor Authentication', tr: 'Çok Faktörlü Kimlik Doğrulama' }, href: '/sign/mfa' },
      { label: { en: 'Monofor Identity (Authenticator)', tr: 'Monofor Identity (Doğrulayıcı)' }, href: '/sign/authenticator' },
      { label: { en: 'Directory & sources', tr: 'Dizin ve kaynaklar' }, href: '/sign/directory' },
      { label: { en: 'User lifecycle', tr: 'Kullanıcı yaşam döngüsü' }, href: '/sign/lifecycle' },
      { label: { en: 'Identity automation', tr: 'Kimlik otomasyonu' }, href: '/sign/automation' },
      { label: { en: 'ITDR', tr: 'ITDR' }, href: '/sign/itdr' },
      { label: { en: 'AI agent identity', tr: 'AI ajan kimliği' }, href: '/sign/ai-agents' },
      { label: { en: 'Reporting', tr: 'Raporlama' }, href: '/sign/reporting' },
      { label: { en: 'Security Score', tr: 'Güvenlik Skoru' }, href: '/sign/security-score' },
      { label: { en: 'Identity Intelligence (IVIP)', tr: 'Kimlik İstihbaratı (IVIP)' }, href: '/sign/identity-intelligence' },
      { label: { en: 'MCP Gateway', tr: 'MCP Gateway' }, href: '/sign/mcp-gateway' },
    ],
  },
  {
    name: 'Monopam',
    tagline: { en: 'Privileged Access', tr: 'Ayrıcalıklı Erişim' },
    href: '/pam',
    items: [
      { label: { en: 'Gateway', tr: 'Gateway' }, href: '/pam/gateway' },
      { label: { en: 'Credential vault', tr: 'Kimlik bilgisi kasası' }, href: '/pam/vault' },
      { label: { en: 'Session recording', tr: 'Oturum kaydı' }, href: '/pam/session-recording' },
      { label: { en: 'Just-in-time access', tr: 'Tam zamanında erişim' }, href: '/pam/jit-access' },
      { label: { en: 'Vendor access (VPAM)', tr: 'Tedarikçi erişimi (VPAM)' }, href: '/pam/vendor-access' },
      { label: { en: 'Certificate Manager', tr: 'Sertifika Yöneticisi' }, href: '/pam/certificate-manager' },
      { label: { en: 'Kubernetes & OpenShift', tr: 'Kubernetes ve OpenShift' }, href: '/pam/kubernetes' },
      { label: { en: 'Database access', tr: 'Veritabanı erişimi' }, href: '/pam/database-access' },
      { label: { en: 'Endpoint Privilege Management', tr: 'Uç Nokta Ayrıcalık Yönetimi' }, href: '/pam/epm' },
      { label: { en: 'Secrets management', tr: 'Gizli bilgi yönetimi' }, href: '/pam/secrets-management' },
      { label: { en: 'Linux identity agent', tr: 'Linux kimlik ajanı' }, href: '/pam/linux' },
      { label: { en: 'AD bridging', tr: 'AD bridging' }, href: '/pam/ad-bridging' },
    ],
  },
  {
    name: 'Monosync',
    tagline: { en: 'Identity Governance', tr: 'Kimlik Yönetişimi' },
    href: '/sync',
    items: [
      { label: { en: 'Connectors', tr: 'Konnektörler' }, href: '/sync/connectors' },
      { label: { en: 'API', tr: 'API' }, href: '/sync/api' },
      { label: { en: 'Governance', tr: 'Yönetişim' }, href: '/sync/governance' },
    ],
  },
]

function ProductsMenu() {
  const lang = useLang()
  return (
    <div className="grid grid-cols-3 gap-1 p-3">
      {productGroups.map((group) => (
        <div key={group.name}>
          <Link
            href={group.href}
            className="block rounded-xl px-4 py-3 transition hover:bg-surface-muted"
          >
            <div className="text-body-md font-medium text-ink-primary">
              {group.name}
            </div>
            <div className="mt-0.5 text-caption uppercase tracking-[0.12em] text-ink-tertiary">
              {group.tagline[lang]}
            </div>
          </Link>
          <ul className="mt-1 space-y-0.5">
            {group.items.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className="block rounded-lg px-4 py-1.5 text-body-sm text-ink-secondary transition hover:bg-surface-muted hover:text-ink-primary"
                >
                  {item.label[lang]}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Solutions mega-menu: use cases + industries
// ---------------------------------------------------------------------------

const solutionsHeaders = {
  byUseCase: { en: 'By use case', tr: 'Kullanım senaryosuna göre' },
  byIndustry: { en: 'By industry', tr: 'Sektöre göre' },
}

const useCaseEntries: { label: Localized; href: string; trOnly?: boolean }[] = [
  { label: { en: 'AI agent security', tr: 'AI ajan güvenliği' }, href: '/solutions/ai-security' },
  { label: { en: 'Machine identity', tr: 'Makine kimliği' }, href: '/solutions/machine-identity' },
  { label: { en: 'Identity security posture', tr: 'Kimlik güvenliği duruşu' }, href: '/solutions/identity-security-posture' },
  { label: { en: 'DevOps privileged access', tr: 'DevOps ayrıcalıklı erişimi' }, href: '/solutions/devops-privileged-access' },
  { label: { en: 'Yerli kimlik yönetimi', tr: 'Yerli kimlik yönetimi' }, href: '/solutions/yerli-kimlik-yonetimi', trOnly: true },
  { label: { en: 'Kamu için kimlik yönetimi', tr: 'Kamu için kimlik yönetimi' }, href: '/solutions/kamu-kimlik-yonetimi', trOnly: true },
  { label: { en: 'BDDK denetimine hazır erişim', tr: 'BDDK denetimine hazır erişim' }, href: '/solutions/bddk-erisim-yonetimi', trOnly: true },
  { label: { en: 'Zero Trust', tr: 'Sıfır Güven' }, href: '/solutions/zero-trust' },
  { label: { en: 'Joiner / Mover / Leaver', tr: 'İşe Giriş / Geçiş / Ayrılış' }, href: '/solutions/joiner-mover-leaver' },
  { label: { en: 'Passwordless & MFA', tr: 'Parolasız ve MFA' }, href: '/solutions/passwordless-mfa' },
  { label: { en: 'Audit & compliance', tr: 'Denetim ve uyum' }, href: '/solutions/audit-compliance' },
  { label: { en: 'External & partner access', tr: 'Dış ve iş ortağı erişimi' }, href: '/solutions/external-partners' },
  { label: { en: 'KVKK compliance', tr: 'KVKK uyumu' }, href: '/compliance/kvkk' },
  { label: { en: 'DORA compliance', tr: 'DORA uyumu' }, href: '/compliance/dora' },
  { label: { en: 'GDPR compliance', tr: 'GDPR uyumu' }, href: '/compliance/gdpr' },
  { label: { en: 'NIS2 compliance', tr: 'NIS2 uyumu' }, href: '/compliance/nis2' },
  { label: { en: 'IEC 62443 compliance', tr: 'IEC 62443 uyumu' }, href: '/compliance/iec-62443' },
  { label: { en: '7545 Siber Güvenlik Kanunu', tr: '7545 Siber Güvenlik Kanunu' }, href: '/compliance/siber-guvenlik-kanunu', trOnly: true },
]

const industryEntries: { label: Localized; href: string }[] = [
  { label: { en: 'Financial Services', tr: 'Finansal Hizmetler' }, href: '/solutions/industries/financial-services' },
  { label: { en: 'Healthcare', tr: 'Sağlık' }, href: '/solutions/industries/healthcare' },
  { label: { en: 'Manufacturing', tr: 'Üretim' }, href: '/solutions/industries/manufacturing' },
  { label: { en: 'Public Sector', tr: 'Kamu' }, href: '/solutions/industries/public-sector' },
  { label: { en: 'Retail', tr: 'Perakende' }, href: '/solutions/industries/retail' },
  { label: { en: 'Telecommunications', tr: 'Telekomünikasyon' }, href: '/solutions/industries/telco' },
]

function SolutionsMenu() {
  const lang = useLang()
  return (
    <div className="grid grid-cols-2 gap-1 p-3">
      <div>
        <div className="px-4 pb-2 pt-2 text-eyebrow font-semibold uppercase text-ink-tertiary">
          {solutionsHeaders.byUseCase[lang]}
        </div>
        <ul className="space-y-0.5">
          {useCaseEntries
            .filter((u) => !u.trOnly || lang === 'tr')
            .map((u) => (
            <li key={u.href}>
              <Link
                href={u.href}
                className="block rounded-lg px-4 py-2 text-body-sm text-ink-secondary transition hover:bg-surface-muted hover:text-ink-primary"
              >
                {u.label[lang]}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="px-4 pb-2 pt-2 text-eyebrow font-semibold uppercase text-ink-tertiary">
          {solutionsHeaders.byIndustry[lang]}
        </div>
        <ul className="space-y-0.5">
          {industryEntries.map((i) => (
            <li key={i.href}>
              <Link
                href={i.href}
                className="block rounded-lg px-4 py-2 text-body-sm text-ink-secondary transition hover:bg-surface-muted hover:text-ink-primary"
              >
                {i.label[lang]}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Resources mega-menu, three columns: For you (personas), Learn, Brand & docs
// Surfaces the CMS-driven Features list inside the Learn column when present.
// ---------------------------------------------------------------------------

const resourcesHeaders = {
  forYou: { en: 'For you', tr: 'Sizin için' },
  learn: { en: 'Learn', tr: 'Öğrenin' },
  supportBrand: { en: 'Support & brand', tr: 'Destek ve marka' },
  features: { en: 'Features', tr: 'Özellikler' },
}

const personaEntries: { label: Localized; href: string }[] = [
  { label: { en: 'IT admins', tr: 'BT yöneticileri' }, href: '/for/it-admins' },
  { label: { en: 'Security engineers', tr: 'Güvenlik mühendisleri' }, href: '/for/security-engineers' },
  { label: { en: 'Developers', tr: 'Geliştiriciler' }, href: '/for/developers' },
  { label: { en: 'Compliance officers', tr: 'Uyum sorumluları' }, href: '/for/compliance-officers' },
  { label: { en: 'End users', tr: 'Son kullanıcılar' }, href: '/for/end-users' },
]

const learnEntries: { label: Localized; href: string }[] = [
  { label: { en: 'Learn Monofor', tr: "Monofor'u öğrenin" }, href: '/learn' },
  { label: { en: 'SSO into AWS', tr: "AWS'ye SSO" }, href: '/learn/sso-aws-saml' },
  { label: { en: 'MFA on VPN', tr: "VPN'de MFA" }, href: '/learn/mfa-on-vpn' },
  { label: { en: 'Windows passwordless', tr: 'Windows parolasız giriş' }, href: '/learn/windows-passwordless-login' },
  { label: { en: 'REST API getting started', tr: "REST API'ye başlangıç" }, href: '/learn/rest-api-getting-started' },
  { label: { en: 'Active Directory sync', tr: 'Active Directory senkronizasyonu' }, href: '/learn/active-directory-sync' },
  { label: { en: 'Audit logs → SIEM', tr: 'Denetim logları → SIEM' }, href: '/learn/audit-log-siem-export' },
  { label: { en: 'Migrate from ADFS', tr: "ADFS'ten geçiş" }, href: '/learn/migrate-from-adfs' },
  { label: { en: 'Migrate from Okta', tr: "Okta'dan geçiş" }, href: '/learn/migrate-from-okta' },
]

const supportEntries: { label: Localized; href: string; external: boolean }[] = [
  { label: { en: 'Blog', tr: 'Blog' }, href: '/blog', external: false },
  { label: { en: 'Glossary', tr: 'Sözlük' }, href: '/glossary', external: false },
  { label: { en: 'PAM TCO Calculator', tr: 'PAM TCO Hesaplayıcı' }, href: '/tools/pam-tco-calculator', external: false },
  { label: { en: 'Compliance Checklist', tr: 'Uyum Kontrol Listesi' }, href: '/tools/identity-compliance-checklist', external: false },
  { label: { en: 'AD Service Account Scanner', tr: 'AD Servis Hesabı Tarayıcı' }, href: '/tools/ad-service-account-scanner', external: false },
  { label: { en: 'Documentation', tr: 'Dokümantasyon' }, href: 'https://help.monofor.com', external: true },
  { label: { en: 'Press kit', tr: 'Basın kiti' }, href: '/press-kit', external: false },
  { label: { en: 'Videos', tr: 'Videolar' }, href: 'https://www.youtube.com/@monofor', external: true },
]

function ResourcesMenu({ features }: { features: any[] }) {
  const lang = useLang()
  return (
    <div className="grid grid-cols-2 gap-1 p-3">
      <div>
        <div className="px-4 pb-2 pt-2 text-eyebrow font-semibold uppercase text-ink-tertiary">
          {resourcesHeaders.forYou[lang]}
        </div>
        <ul className="space-y-0.5">
          {personaEntries.map((p) => (
            <li key={p.href}>
              <Link
                href={p.href}
                className="block rounded-lg px-4 py-2 text-body-sm text-ink-secondary transition hover:bg-surface-muted hover:text-ink-primary"
              >
                {p.label[lang]}
              </Link>
            </li>
          ))}
        </ul>
        <div className="mt-3 border-t border-line-subtle px-4 pb-2 pt-3 text-eyebrow font-semibold uppercase text-ink-tertiary">
          {resourcesHeaders.learn[lang]}
        </div>
        <ul className="space-y-0.5">
          {learnEntries.map((l) => (
            <li key={l.href}>
              <Link
                href={l.href}
                className="block rounded-lg px-4 py-2 text-body-sm text-ink-secondary transition hover:bg-surface-muted hover:text-ink-primary"
              >
                {l.label[lang]}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="px-4 pb-2 pt-2 text-eyebrow font-semibold uppercase text-ink-tertiary">
          {resourcesHeaders.supportBrand[lang]}
        </div>
        <ul className="space-y-0.5">
          {supportEntries.map((r) => (
            <li key={r.href}>
              {r.external ? (
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-lg px-4 py-2 text-body-sm text-ink-secondary transition hover:bg-surface-muted hover:text-ink-primary"
                >
                  {r.label[lang]}
                </a>
              ) : (
                <Link
                  href={r.href}
                  className="block rounded-lg px-4 py-2 text-body-sm text-ink-secondary transition hover:bg-surface-muted hover:text-ink-primary"
                >
                  {r.label[lang]}
                </Link>
              )}
            </li>
          ))}
        </ul>
        {features.length > 0 && (
          <>
            <div className="mt-3 border-t border-line-subtle px-4 pb-2 pt-3 text-eyebrow font-semibold uppercase text-ink-tertiary">
              {resourcesHeaders.features[lang]}
            </div>
            <ul className="space-y-0.5">
              {features.slice(0, 5).map((f: any) => (
                <li key={f.slug}>
                  <Link
                    href={'/features/' + f.slug}
                    className="block rounded-lg px-4 py-2 text-body-sm text-ink-secondary transition hover:bg-surface-muted hover:text-ink-primary"
                  >
                    {f.title}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline glyphs (currentColor for theme-friendly fill/stroke)
// ---------------------------------------------------------------------------

function ChevronGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function MenuGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 7h18M3 12h18M3 17h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function CloseGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
